import React from 'react';
import type { ReactNode } from 'react';
import Image from 'next/image';
import Link from 'next/link.js';
import Router from 'next/router';
import { Seo } from '@components/Seo';
import { LearnMore } from '@components/layout/Footer/LearnMore';
import { Button } from '@components/main-components/button/Button';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useTieredUpload } from '@hooks/useTieredUpload';
import { EXTERNAL_PATHS, NAV_LINKS } from '@utils/constants';

export default function About() {
  return (
    <>
      <Seo />
      <div className="flex w-full flex-col items-center md:pb-24">
        <Hero />
        <Section1 />
        <VideoSection />
        <Section2 />
        <div className="w-full max-w-[1280px] md:px-8">
          <LearnMore learnMore={undefined} />
        </div>
      </div>
    </>
  );
}

const Hero = () => {
  const { isAuthenticatedWithAPI, authUser } = useAuth();
  const { login } = useConnectedAuth();

  const { createDraft, loading } = useTieredUpload();

  return (
    <div className="flex w-full flex-col items-center bg-[#000000] text-white">
      <div className="relative flex w-full max-w-[1280px] flex-col-reverse items-center gap-24 bg-none bg-contain bg-right bg-no-repeat px-8 pb-16 pt-32 sm:pb-24 md:flex-col-reverse md:px-16 lg:flex-row lg:bg-[url('https://d16zqvkniqf1rn.cloudfront.net/hero.png')] lg:pt-48">
        <div className="flex flex-col gap-12 sm:w-full md:w-full lg:w-1/2 ">
          <div className="flex flex-col gap-4 text-center md:text-left">
            <h1 className="font-title text-title-xl md:text-title-xxl">
              Your new home for music discovery
            </h1>
            <p className="font-base text-base-xl opacity-80">
              A collaborative movement that reimagines the relationship between artists and their
              fans.
            </p>
          </div>
          <div className="flex w-full flex-row justify-center gap-x-4 md:justify-start">
            <Button
              variant="fuchsia"
              className="w-full min-w-min max-w-none sm:max-w-fit "
              size="M"
              type="button"
              label="Explore Sounds"
              href={NAV_LINKS.Home}
            />
            <Button
              variant="secondary"
              className="w-full min-w-min max-w-none sm:max-w-fit"
              size="M"
              type="button"
              label="Drop with us"
              loading={loading}
              disabled={loading}
              onClick={ev => {
                ev.preventDefault();

                if (!isAuthenticatedWithAPI) {
                  login();
                  return;
                }

                if (!authUser?.artist) {
                  Router.push(NAV_LINKS.ArtistApplication);
                  return;
                }

                // This is already handling errors
                createDraft();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ children }: { children: ReactNode }) => {
  return (
    <div className="align-center relative flex max-w-[1280px] flex-col items-start gap-8 p-8 md:flex-col md:gap-16 md:p-16 lg:flex-row lg:gap-24 lg:p-24">
      {children}
    </div>
  );
};

const Section1 = () => {
  return (
    <Section>
      <div className="relative aspect-square w-full sm:w-full md:w-1/2 lg:w-1/2">
        <Image
          src="https://d16zqvkniqf1rn.cloudfront.net/artists.png"
          alt="Picture showing artists on Sound.xyz"
          fill
          priority
          sizes="600px"
          objectFit="contain"
        />
      </div>
      <div className="flex flex-col md:w-full lg:w-1/2">
        <H2>Putting listeners in the front seat</H2>
        <H3>Discover and collect your favorite songs</H3>
        <P>Listen for free, and collect to stake your claim on being there before everyone else.</P>
        <H3>Directly support your favorite artists</H3>
        <P>
          100% of your support goes directly to the artist. No middleman, so they can focus on
          creating incredible music.
        </P>
        <H3>Connect with artists and unlock exclusive perks</H3>
        <P>
          Build a relationship with the artists you love, and gain access to unique rewards by
          collecting.
        </P>
      </div>
    </Section>
  );
};

const VideoSection = () => {
  return (
    <div className="w-full max-w-[1280px] p-8">
      <div className="flex w-full flex-col items-center text-center">
        <h2 className="mb-8 text-center font-title text-title-xl font-medium md:text-title-xxl">
          Empowering artists to own their music
        </h2>
      </div>
      <div className="overflow-hidden rounded-lg bg-black">
        <video autoPlay loop muted controls style={{ width: '100%', height: '100%' }}>
          <source src="https://d16zqvkniqf1rn.cloudfront.net/opening-up-sound/main-v6.mp4" />
        </video>
      </div>
    </div>
  );
};

const Section2 = () => {
  return (
    <Section>
      <div className="flex flex-col md:w-full lg:w-1/2">
        <H2>Built by a passionate, mighty team</H2>
        <P>
          Backed by a16z Crypto, Snoop Dogg, Ryan Tedder, and many more, we’re a tight-knit,
          distributed, nimble team with diverse backgrounds from Apple, Opendoor, OpenSea, Pandora,
          and Slack.
        </P>
        <P>
          Our mission is to create a world where artists can make a living off their music alone,
          and where listeners play an active role in music discovery. If this resonates with you, we
          would love for you to join us in making this a reality!&nbsp;
          <Link
            className="underline hover:font-medium"
            href={EXTERNAL_PATHS.Careers}
            target="_blank"
          >
            View our open roles and apply!
          </Link>
        </P>
      </div>
      <div className="relative aspect-square w-full md:w-full lg:w-1/2">
        <Image
          src="https://d16zqvkniqf1rn.cloudfront.net/people.png"
          alt="Sound.xyz team"
          fill
          objectFit="contain"
        />
      </div>
    </Section>
  );
};

const P = ({ children }: { children: ReactNode }) => {
  return (
    <p className="mb-8 text-center font-base text-base-l leading-8 md:text-left">{children}</p>
  );
};

const H2 = ({ children }: { children: ReactNode }) => {
  return (
    <h2 className="mb-8 text-center font-title text-title-xl font-medium md:text-left md:text-title-xxl">
      {children}
    </h2>
  );
};

const H3 = ({ children }: { children: ReactNode }) => {
  return (
    <h2 className="mb-2 text-center font-title text-title-m font-medium md:text-left md:text-title-l">
      {children}
    </h2>
  );
};
